
import { config, debug, george, helpers, NetbankingAPI, observable, observer, pr, React, toJS } from "aaa";
import { FeatureRoutes } from "components/app/AppRoutes";
import { AppointmentRoutes } from "components/appointment/AppointmentRoutes";
import { BackBehaviour, defaultBackBehaviour, georgeWebHeaderStore } from "components/common/GeorgeWebHeader";
import { TopBackgroundColor } from "components/util/TopBackgroundColor";
import * as gitVersion from "gitVersion";
import * as _ from "lodash";
import { NetbankingAPIClient } from "network/APIClient";
import { Link, RouteComponentProps } from "react-router-dom";
import { AccountsStore } from "stores/AccountsStore";
import authStore from "stores/AuthStore";
import { CardsStore } from "stores/CardsStore";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { OffersStore } from "stores/OffersStore";
import { pushRoute } from "stores/RouterFunctions";
import { history } from "stores/RouterStore";
import { IBuildingsAccount, IAccount, supportsPockets } from "types/account";
import { isBankCard, isCreditCard, supportsActivateCard, supportsDisplayPin, supportsGeoControl, supportsQuickDrawDown, supportsReissueCard, supportsReissueCardAndUpgrade, supportsUpgrade } from "types/card";

const _offersStore = new OffersStore();
const _cardsStore = new CardsStore();
const _accountsStore = new AccountsStore();

@observer
export default class Component extends React.Component<RouteComponentProps<{}>, {}> {
    @observable clientId = "";
    @observable backBehaviour: BackBehaviour = defaultBackBehaviour;
    @observable channel = "";
    @observable creditCardUpgradeFirstItem = "";
    @observable creditCardUpgradeSecondItem = "";
    @observable buildingsAccounts: IBuildingsAccount[] = [];
    @observable isLoadingBuildingsAccounts = true;
    @observable selectedBuildingsAccount?: IBuildingsAccount;
    @observable savingsAccounts: IAccount[] = [];
    @observable isLoadingSavingsAccounts = true;
    @observable selectedSavingsAccount?: IAccount;

    async componentDidMount() {
        if (!this.isAuthorized()) {
            pushRoute("/");
        }

        // Hack for Mladen -> don't automatically show error screens for unauthorized and forbidden
        authStore.handleUnauthorized = false;
        authStore.handleForbidden = false;
        await Promise.all(externalInterfaceStore.isGeorgeBusiness
            ? [
                this.loadCards(),
            ] : [
                this.loadOffers(),
                this.loadCards(),
                this.loadAccounts(),
                this.loadBuildingsAccounts(),
                this.loadSavingsAccounts(),
            ]);

        // In FeatureTests handleUnauthorized is false, so we check if failed to
        // load either cards or offers. If both calls failes -> got to login screen
        if (!_offersStore.offers
            && !_cardsStore.cards
        ) {
            authStore.logout();
            pushRoute("/");
        }

        authStore.handleUnauthorized = true;
        authStore.handleForbidden = true;
    }

    componentWillUnmount() {
        georgeWebHeaderStore.configure({ backBehaviour: defaultBackBehaviour });
    }

    loadOffers = async () => {
        try {
            await _offersStore.load();
            _offersStore.filterShopOffers();
            debug.log("### offers loaded", toJS(_offersStore.offers));
        } catch (err) {
            debug.log("### error loading offers", err);
        }
    }

    loadCards = async () => {
        try {
            await _cardsStore.load();
            debug.log("### cards loaded", toJS(_cardsStore.cards));
        } catch (err) {
            debug.log("### error loading cards", err);
        }
    }

    loadAccounts = async () => {
        try {
            await _accountsStore.load();
            debug.log("### accounts loaded", toJS(_accountsStore.accounts));
        } catch (err) {
            debug.log("### error loading accounts", err);
        }
    }

    loadBuildingsAccounts = async () => {
        try {
            this.isLoadingBuildingsAccounts = true;
            const target = NetbankingAPI.getBuildingsAccounts();
            this.buildingsAccounts = await NetbankingAPIClient.requestType(target) ?? [];
            this.selectedBuildingsAccount = this.buildingsAccounts[0];
            debug.log("### accounts loaded", toJS(_accountsStore.accounts));
        } catch (err) {
            debug.log("### error loading accounts", err);
        } finally {
            this.isLoadingBuildingsAccounts = false;
        }
    }

    loadSavingsAccounts = async () => {
        try {
            this.isLoadingSavingsAccounts = true;
            const target = NetbankingAPI.getAccounts();
            const allAccounts = await NetbankingAPIClient.requestType(target) ?? [];
            // Filter for savings accounts (type === "SAVING")
            this.savingsAccounts = allAccounts.filter(account => account.type === "SAVING");
            this.selectedSavingsAccount = this.savingsAccounts[0];
            debug.log("### savings accounts loaded", toJS(this.savingsAccounts));
        } catch (err) {
            debug.log("### error loading savings accounts", err);
        } finally {
            this.isLoadingSavingsAccounts = false;
        }
    }

    renderCardFeatures() {
        if (!_cardsStore.selectedCard) {
            return;
        }

        const isGeorgeBusiness = externalInterfaceStore.isGeorgeBusiness;

        // Add new card feature here
        const cardFeatures = [{
            enabled: !isGeorgeBusiness && config.PIN_RESET_ENABLED,
            check: supportsDisplayPin,
            label: "Pin Reset",
            url: "/pin-reset",
        }, {
            enabled: !isGeorgeBusiness && config.PIN_RESET_ENABLED,
            check: supportsDisplayPin,
            label: "Pin Reset Debug",
            url: "/pin-reset",
            query: { debug: true }
        }, {
            enabled: !isGeorgeBusiness && config.CHANGE_REPAYMENT_ENABLED,
            check: isCreditCard,
            label: "Zahlungsmodalität",
            url: "/change-repayment",
        }, {
            enabled: !isGeorgeBusiness && config.QUICK_DRAWDOWN_ENABLED,
            check: supportsQuickDrawDown,
            label: "Übertrag auf Konto",
            url: "/quick-drawdown",
        }, {
            enabled: !isGeorgeBusiness && config.GEO_CONTROL_ENABLED,
            check: supportsGeoControl,
            label: "Geo Control",
            url: "/geo-control",
        }, {
            enabled: !isGeorgeBusiness && config.REISSUE_CARD_ENABLED,
            check: supportsReissueCard,
            label: "Karte nachbestellen",
            url: "/reissue-card",
        }, {
            enabled: !isGeorgeBusiness && config.REISSUE_CARD_UPGRADE_ENABLED,
            check: supportsReissueCardAndUpgrade,
            label: "Kreditkarten-Umstieg/nachbestellen",
            url: "/reissue-card-upgrade",
        }, {
            enabled: !isGeorgeBusiness && config.ACTIVATE_CARD_ENABLED,
            check: supportsActivateCard,
            label: "Karte aktivieren",
            url: "/activate-card",
        }, {
            enabled: !isGeorgeBusiness && config.LINK_ACCOUNT,
            check: isBankCard,
            label: "Verknüpfte Konten",
            url: "/link-account"
        }, {
            enabled: !isGeorgeBusiness && config.SHOW_CARD_DATA,
            check: isCreditCard,
            label: "Kartendaten anzeigen",
            url: "/show-card-data"
        }, {
            enabled: config.LOYALTY_ENABLED,
            check: isCreditCard,
            label: "Loyalty",
            url: "/cc-loyalty"
        }, {
            enabled: !isGeorgeBusiness && config.CREDIT_CARD_UPGRADE_ENABLED,
            check: supportsUpgrade,
            label: "Upgrade",
            url: "/cc-upgrade"
        }];

        const features = _.compact(cardFeatures.map(feature => {
            if (feature.enabled && _cardsStore.selectedCard && feature.check(_cardsStore.selectedCard)) {
                return (
                    <div key={feature.url + feature.label}>
                        <a
                            style={{ textDecoration: "underline", cursor: "pointer" }}
                            className="theme-paragraph-color"
                            onClick={() => history.push(debug.withAccessToken(feature.url, {
                                ...feature.query,
                                cardId: _cardsStore.selectedCardId,
                                clientId: this.clientId,
                                channel: this.channel
                            }))}
                        >
                            {feature.label}
                        </a>
                    </div >
                );
            }
        }));

        return (
            <div>
                <br />
                {features}
            </div>
        );
    }

    renderAccountFeatures() {
        if (!_accountsStore.selectedAccount) {
            return;
        }

        // Add new card feature here
        const accountFeatures = [{
            enabled: config.PIN_RESET_ENABLED,
            check: supportsPockets,
            label: "Sparziele aktivieren",
            url: "/pockets",
        }];

        const features = _.compact(accountFeatures.map(feature => {
            if (feature.enabled && _accountsStore.selectedAccount && feature.check(_accountsStore.selectedAccount)) {
                return (
                    <div key={feature.url + feature.label}>
                        <a
                            style={{ textDecoration: "underline", cursor: "pointer" }}
                            className="theme-paragraph-color"
                            onClick={() => history.push(debug.withAccessToken(feature.url, {
                                // ...feature.query,
                                accountId: _accountsStore.selectedAccount?.id,
                                clientId: this.clientId,
                                channel: this.channel
                            }))}
                        >
                            {feature.label}
                        </a>
                    </div >
                );
            }
        }));

        return (
            <div>
                <br />
                {features}
            </div>
        );
    }

    renderBuildingsAccountFeatures() {
        if (!this.selectedBuildingsAccount) {
            return;
        }

        // Add new card feature here
        const buildingsAccountFeatures = [{
            enabled: config.BSP_OPTIONS_ENABLED,
            label: "Bausparer Optionen",
            url: FeatureRoutes.BSP_OPTIONS,
        }, {
            enabled: config.BSP_OPTIONS_PAYOUT_ENABLED,
            label: "Bausparer Optionen Payout",
            url: FeatureRoutes.BSP_OPTIONS_PAYOUT,
        }];

        const features = _.compact(buildingsAccountFeatures.map(feature => {
            if (feature.enabled) {
                return (
                    <div key={feature.url + feature.label}>
                        <a
                            style={{ textDecoration: "underline", cursor: "pointer" }}
                            className="theme-paragraph-color"
                            onClick={() => history.push(debug.withAccessToken(feature.url, {
                                // ...feature.query,
                                contractId: this.selectedBuildingsAccount?.id,
                                clientId: this.clientId,
                                channel: this.channel
                            }))}
                        >
                            {feature.label}
                        </a>
                    </div >
                );
            }
        }));

        return (
            <div>
                <br />
                {features}
            </div>
        );
    }

    onLinkAccount = async () => {
        debug.log("### onLinkAccount", _cardsStore.selectedCard);
        if (_cardsStore.selectedCardId) {
            history.push(debug.withAccessToken("/link-account", { cardId: _cardsStore.selectedCardId }));
        }
    }

    renderCards() {
        const cards = _cardsStore.cards ? _cardsStore.cards.map(c => ({ value: c.id, label: c.productI18N + " (" + c.number + ")" })) : [];

        return (
            <>
                {_cardsStore.isLoading && <span className="theme-paragraph-color">Lade Karten ... </span>}

                {!_cardsStore.isLoading &&
                    <>
                        <george.DropDown
                            value={_cardsStore.selectedCardId}
                            onChange={(event: any, index: any, value: any) => {
                                _cardsStore.selectedCardId = value;
                            }}
                            maxHeight={200}
                            items={cards}
                        />
                        {this.renderCardFeatures()}
                    </>
                }
            </>
        );
    }

    renderAccounts() {
        const accounts = _accountsStore.accounts ? _accountsStore.accounts.map(account => ({
            value: account.id,
            label: account.productI18N + " (" + account.accountno.iban + ")"
        })) : [];

        return (
            <>
                {_accountsStore.isLoading && <span className="theme-paragraph-color">Lade Karten ... </span>}

                {!_accountsStore.isLoading &&
                    <>
                        <george.DropDown
                            value={_accountsStore.selectedAccount?.id}
                            onChange={(event: any, index: any, value: any) => {
                                _accountsStore.selectedAccount = _accountsStore.accounts[index];
                            }}
                            maxHeight={200}
                            items={accounts}
                        />
                        {this.renderAccountFeatures()}
                    </>
                }
            </>
        );
    }

    renderSavingsAccountFeatures() {
        if (!this.selectedSavingsAccount) {
            return;
        }

        // Add new savings account feature here
        const savingsAccountFeatures = [{
            enabled: config.SAVINGS_OPTIONS_ENABLED,
            label: "Sparbücher Optionen",
            url: FeatureRoutes.SAVINGS_OPTIONS,
        }];

        const features = _.compact(savingsAccountFeatures.map(feature => {
            if (feature.enabled) {
                return (
                    <div key={feature.url + feature.label}>
                        <a
                            style={{ textDecoration: "underline", cursor: "pointer" }}
                            className="theme-paragraph-color"
                            onClick={() => history.push(debug.withAccessToken(feature.url, {
                                accountId: this.selectedSavingsAccount?.id,
                                clientId: this.clientId,
                                channel: this.channel
                            }))}
                        >
                            {feature.label}
                        </a>
                    </div >
                );
            }
        }));

        return (
            <div>
                <br />
                {features}
            </div>
        );
    }

    renderSIdentity() {
        const useCases = [{
            query: "-",
            title: "Welche clientID/useragent?"
        }, {
            query: "clientID=transactionapp",
            title: "transactionapp"
        }, {
            query: "clientID=transactionapp&useragent=android",
            title: "transactionapp android"
        }, {
            query: "clientID=transactionapp&useragent=ios",
            title: "transactionapp ios"
        }, {
            query: "clientID=georgeclient",
            title: "georgeclient"
        }, {
            query: "clientID=georgeclient&useragent=android",
            title: "georgeclient android"
        }, {
            query: "clientID=georgeclient&useragent=ios",
            title: "georgeclient ios"
        }, {
            query: "clientID=georgeclient&useragent=windows",
            title: "georgeclient windows"
        }, {
            query: "clientID=georgeclient&useragent=macos",
            title: "georgeclient macos"
        }];

        const menuItems = useCases.map(c => (
            { value: c.query, label: c.title }
        ));

        return (
            <george.DropDown
                value="-"
                onChange={(event: any, index: any, value: any) => {
                    if (value !== "-") {
                        history.push(
                            debug.withAccessToken("/sidentity", value)
                        );
                    }
                }}
                maxHeight={200}
                items={menuItems}
            />
        );
    }

    renderOffers() {
        let offers;
        if (_offersStore.offers && _offersStore.offers.length > 0) {
            offers = _offersStore.offers.map(offer => {
                const params: any = {
                    productId: offer.productId,
                    conditionsGroupId: offer.conditionsGroupId,
                    channelProduct: offer.channelProduct,
                };

                if (this.clientId) {
                    params.clientId = this.clientId;
                } else if (this.channel) {
                    params.channel = this.channel;
                }

                return (
                    <div key={offer.productId + offer.conditionsGroupId + offer.channelProduct}>
                        <Link
                            className="theme-paragraph-color"
                            to={debug.withAccessToken("/shop", params)}
                        >
                            {offer.productName/*+ ", " + offer.conditionsGroupName + ",  " + offer.channelProduct*/}
                        </Link>
                    </div>
                );
            });
        }

        return (
            <div>
                <h4 className="theme-title-color">Offers</h4>
                {_offersStore.isLoading && !_offersStore.offers && <span>Lade Angebote ...</span>}
                {_offersStore.offers && _offersStore.offers.length === 0 && <span><br />Fehler oder keine Angebote gefunden.</span>}
                {!_offersStore.isLoading && _offersStore.offers && <>{offers}</>}
            </div>
        );
    }

    isAuthorized() {
        return authStore.accessToken && helpers.isUid(authStore.accessToken);
    }

    // Render georgeIcons that are imported in index.html by name. E.g. renderIcon("circle-pie")
    renderIcon(name: string) {
        return (
            <svg
                width={24}
                height={24}
                stroke="red"
                fill="red"
                focusable="false"
            >
                <use xlinkHref={`#ico-${name}`} />
            </svg>
        );
    }

    renderClientIdSelection() {
        const RENDER_CHANNEL_SELECTION = false;
        return (
            <>
                Wähle die zu verwendende ClientID<br /><br />
                <george.DropDown
                    value={this.clientId}
                    onChange={(event: any, index: any, value: any) => {
                        this.clientId = value;
                        if (this.clientId) {
                            this.channel = "";
                        }
                    }}
                    maxHeight={200}
                    items={[{
                        value: "",
                        label: "Select clientID"
                    }, {
                        value: "transactionapp",
                        label: "app (tranactionapp)"
                    }, {
                        value: "georgeclient",
                        label: "web (georgeclient)"
                    }]}
                />
                {RENDER_CHANNEL_SELECTION && <>
                    <george.DropDown
                        value={this.channel}
                        onChange={(event: any, index: any, value: any) => {
                            this.channel = value;
                            if (this.channel) {
                                this.clientId = "";
                            }
                        }}
                        maxHeight={200}
                        items={[{
                            value: "",
                            label: "Select channel"
                        }, {
                            value: "10",
                            label: "10 (tranactionapp)"
                        }, {
                            value: "09",
                            label: "09 (georgeclient)"
                        }]}
                    />
                </>}
            </>
        );
    }

    renderFeature(url: string, title: string, isGeorgeBusinessEnabled?: boolean) {
        if (externalInterfaceStore.isGeorgeBusiness && !isGeorgeBusinessEnabled) {
            return null;
        }

        const FeatureContainer = pr.styledDiv({ marginBottom: 16 });
        let params: any = {};
        if (this.clientId) {
            params.clientId = this.clientId;
        } else if (this.channel) {
            params.channel = this.channel;
        }

        // TODO: Remove once we have final params
        // params.id = "1c9214fa-a96f-4d01-bd5b-53474d35cc56";

        return (
            <FeatureContainer>
                <Link className="theme-paragraph-color" to={debug.withAccessToken(url, params)}>{title}</Link>
            </FeatureContainer>
        );
    }

    renderBuildingsAccounts() {
        const buildingsAccounts = this.buildingsAccounts.map(buildingsAccount => ({
            value: buildingsAccount.id,
            label: buildingsAccount.productI18N + " (" + buildingsAccount.id + ")"
        }));

        return (
            <>
                {this.isLoadingBuildingsAccounts && <span className="theme-paragraph-color">Lade Bausparer ... </span>}

                {!this.isLoadingBuildingsAccounts && this.buildingsAccounts.length > 0 && (
                    <>
                        <george.DropDown
                            value={this.selectedBuildingsAccount?.id}
                            onChange={(event: any, index: any, value: any) => {
                                this.selectedBuildingsAccount = this.buildingsAccounts[index];
                            }}
                            maxHeight={200}
                            items={buildingsAccounts}
                        />
                        {this.renderBuildingsAccountFeatures()}
                    </>
                )}
                {!this.isLoadingBuildingsAccounts && this.buildingsAccounts.length === 0 && (
                    <span className="theme-paragraph-color">Keine Bausparer verfügbar</span>
                )}
            </>
        );
    }

    renderSavingsAccounts() {
        const savingsAccounts = this.savingsAccounts.map(savingsAccount => ({
            value: savingsAccount.id,
            label: savingsAccount.productI18N + " (" + savingsAccount.id + ")"
        }));

        return (
            <>
                {this.isLoadingSavingsAccounts && <span className="theme-paragraph-color">Lade Sparbücher ... </span>}

                {!this.isLoadingSavingsAccounts && this.savingsAccounts.length > 0 && (
                    <>
                        <george.DropDown
                            value={this.selectedSavingsAccount?.id}
                            onChange={(event: any, index: any, value: any) => {
                                this.selectedSavingsAccount = this.savingsAccounts[index];
                            }}
                            maxHeight={200}
                            items={savingsAccounts}
                        />
                        {this.renderSavingsAccountFeatures()}
                    </>
                )}
                {!this.isLoadingSavingsAccounts && this.savingsAccounts.length === 0 && (
                    <span className="theme-paragraph-color">Keine Sparbücher verfügbar</span>
                )}
            </>
        );
    }

    render() {
        // const pathComponents = window.location.href.split("#");
        // debug.log("### root", pathComponents[0]);

        const FeatureContainer = pr.styledDiv({ marginBottom: 30 });

        return (
            <div className="theme-landing-background-color">
                <TopBackgroundColor color="silver" />
                <div style={{ fontSize: 16, background: "silver", padding: 10 }}>
                    branch: {gitVersion.GIT_BRANCH} - version: {gitVersion.GIT_VERSION}
                </div>

                <div style={{ padding: 20, fontSize: 20 }}>
                    <div>
                        <br />
                        <a
                            className="theme-paragraph-color"
                            style={{ textDecoration: "underline", cursor: "pointer" }}
                            onClick={async () => {
                                await externalInterfaceStore.logout();
                                pushRoute("/");
                            }}
                        >
                            Logout
                        </a>

                        <h4 className="theme-title-color"><i>Willkommen in der Hybrid Testapp</i></h4>

                        {this.renderClientIdSelection()}

                        {externalInterfaceStore.isGeorgeWeb && (
                            <>
                                <h4 className="theme-title-color">Webheader "Zurück"-Button Verhalten</h4>
                                <george.DropDown
                                    value={this.backBehaviour}
                                    onChange={(event: any, index: any, value: any) => {
                                        this.backBehaviour = value;

                                        georgeWebHeaderStore.configure({
                                            backBehaviour: value
                                        });
                                    }}
                                    maxHeight={200}
                                    items={[{
                                        value: "none",
                                        label: "None"
                                    }, {
                                        value: "tokenExchange",
                                        label: "Token Exchange"
                                    }, {
                                        value: "revoke",
                                        label: "Revoke"
                                    }]}
                                />
                            </>
                        )}

                        <h4 className="theme-title-color">Features</h4>

                        {this.renderFeature(AppointmentRoutes.ROOT, "Make an appointment")}
                        {this.renderFeature("/googlepay", "Google Pay")}
                        {this.renderFeature("/ichapp/settings", "Ich App")}
                        {this.renderFeature("/fit", "George FIT")}
                        {this.renderFeature("/survey", "Survey")}
                        {this.renderFeature("/pension-insurance", "Pensionsvorsorge")}
                        {this.renderFeature("/accident-insurance", "s Unfall-Schutz - Ihr 24-Stunden-Schutz")}
                        {this.renderFeature("/life-insurance", "s Ablebens-Schutz - Die finanzielle Absicherung für Ihre Hinterbliebenen")}
                        {this.renderFeature("/pension-funds", "s Privat-Pension mit Prämien-Plus")}
                        {this.renderFeature("/household-insurance", "Haushaltsversicherung")}
                        {this.renderFeature("/car-leasing", "Car leasing")}
                        {this.renderFeature("/tpp", "Third Party Providers")}
                        {this.renderFeature("/asset-calculation", "Asset Calculation")}
                        {this.renderFeature("/gig", "Geschäftsinformationen George")}

                        {this.renderFeature("/usermanagement", "Manage alias")}
                        {this.renderFeature("/login-management", "Login Management (Redirect)")}
                        {this.renderFeature("/sidentitysettings", "sID Settings Page")}
                        {this.renderFeature("/gid", "gID Settings Page (should show error)")}
                        {this.renderFeature("/gid/limit", "gID Onboarding Page")}
                        {this.renderFeature("/gid/limit-management", "gID Limit Management Page")}
                        {this.renderFeature("/gid/usermanagement", "gID User Management Page")}
                        {this.renderFeature("/optiopay-activation", "OptioPay Activation")}
                        {this.renderFeature("/optiopay", "OptioPay")}
                        {this.renderFeature("/deferral-service", "Deferral Service")}
                        {this.renderFeature("/teamcard-linkage", "Teamcard Linkage", true)}
                        {this.renderFeature(FeatureRoutes.JUNIOR, "gJunior", config.JUNIOR)}
                        {this.renderFeature(FeatureRoutes.TRADING_PREFERENCES, "Trading Preferences", config.TRADING_PREFERENCES_ENABLED)}

                        <h4 className="theme-title-color">Card Features</h4>
                        <FeatureContainer>{this.renderCards()}</FeatureContainer>

                        {!externalInterfaceStore.isGeorgeBusiness && (
                            <>
                                <h4 className="theme-title-color">s Identity Onboarding</h4>
                                <FeatureContainer>
                                    {this.renderSIdentity()}
                                </FeatureContainer>

                                <h4 className="theme-title-color">Account Features</h4>
                                <FeatureContainer>{this.renderAccounts()}</FeatureContainer>

                                <h4 className="theme-title-color">Bausparer</h4>
                                <FeatureContainer>
                                    {this.renderBuildingsAccounts()}
                                </FeatureContainer>

                                <h4 className="theme-title-color">Sparbücher</h4>
                                <FeatureContainer>
                                    {this.renderSavingsAccounts()}
                                </FeatureContainer>

                                <h4 className="theme-title-color">Store Use Cases</h4>
                                <FeatureContainer>
                                    {this.renderFeature("/store", "Store")}
                                </FeatureContainer>
                                <FeatureContainer>
                                    <Link className="theme-paragraph-color" to={debug.withAccessToken("/store/image-test")}>Store Images</Link>
                                </FeatureContainer>

                                {config.SHOP_ENABLED &&
                                    <FeatureContainer>{this.renderOffers()}</FeatureContainer>
                                }
                            </>
                        )}

                        <h4 className="theme-title-color">Util Features for internal testing</h4>
                        <FeatureContainer>
                            {this.renderFeature("/handover-url-test", "Handover URL Test")}
                        </FeatureContainer>
                    </div >
                </div>
            </div >
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/test/FeatureTests.tsx